import React, { useState, useEffect } from 'react';
import { User, UserRole } from '../../../types';
import { UsersService } from '../../../services/api/users';
import { useAuth } from '../../../contexts/AuthContext';
import UserForm from '../UserForm/UserForm';
import UserDetail from '../UserDetail/UserDetail';
import { Filter, MoreVertical, Edit, Trash2, Plus, Search, Users as UsersIcon } from 'lucide-react';
import { Button, Badge, LoadingSpinner, PageHeader, EmptyState } from '../../common';

interface ColumnFilters {
  jobTitle: boolean;
  department: boolean;
  roles: boolean;
  status: boolean;
  lastLogin: boolean;
}

const UsersList: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    jobTitle: true,
    department: true,
    roles: true,
    status: true,
    lastLogin: true
  });
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId !== null) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const loadUsers = async () => {
    try {
      const usersData = await UsersService.getUsers(token || undefined);
      // Ensure we have an array before setting state
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      } else {
        console.error('Users API returned non-array:', usersData);
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await UsersService.getRoles(token || undefined);
      // Ensure we have an array before setting state
      if (Array.isArray(rolesData)) {
        setRoles(rolesData);
      } else {
        console.error('Roles API returned non-array:', rolesData);
        setRoles([]);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
      setRoles([]); // Reset to empty array on error
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
  };

  const handleBackFromDetail = () => {
    setSelectedUser(null);
  };

  const handleUserUpdated = () => {
    loadUsers();
  };

  const toggleColumnFilter = (column: keyof ColumnFilters) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingUser(null);
    loadUsers();
  };

  const handleEditUser = (e: React.MouseEvent, user: User) => {
    e.stopPropagation();
    setEditingUser(user);
    setShowForm(true);
    setOpenMenuId(null);
  };

  const handleDeleteUser = async (e: React.MouseEvent, userId: number) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await UsersService.deleteUser(userId, token || undefined);
      loadUsers();
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const toggleMenu = (e: React.MouseEvent, userId: number) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === userId ? null : userId);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.job_title && user.job_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.department && user.department.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getAvatarColor = (name: string) => {
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-700' },
      { bg: 'bg-purple-100', text: 'text-purple-700' },
      { bg: 'bg-green-100', text: 'text-green-700' },
      { bg: 'bg-orange-100', text: 'text-orange-700' },
      { bg: 'bg-pink-100', text: 'text-pink-700' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      { bg: 'bg-teal-100', text: 'text-teal-700' }
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const getRoleColor = (role: string) => {
    switch(role) {
      case 'Administrator': return 'bg-blue-100 text-blue-700';
      case 'Super Administrator': return 'bg-purple-100 text-purple-700';
      case 'Contractor': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRolesBadges = (rolesString?: string) => {
    if (!rolesString) return <span className="text-sm text-gray-400">—</span>;
    const roleNames = rolesString.split(',');
    return (
      <div className="flex flex-wrap gap-1">
        {roleNames.map((role, index) => (
          <span key={index} className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(role.trim())}`}>
            {role.trim()}
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading team members..." />;
  }

  if (selectedUser) {
    return (
      <UserDetail
        user={selectedUser}
        onBack={handleBackFromDetail}
        onUserUpdated={handleUserUpdated}
      />
    );
  }

  return (
    <>
      {/* Header Actions */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
        <Button
          variant="primary"
          size="md"
          icon={<Plus className="w-4 h-4" />}
          onClick={handleCreateUser}
        >
          Add User
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
        <Button
          variant="secondary"
          size="sm"
          icon={<Filter className="w-4 h-4" />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Columns
        </Button>
      </div>

      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-md mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Show/Hide Columns</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columnFilters.jobTitle}
                onChange={() => toggleColumnFilter('jobTitle')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Job Title</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columnFilters.department}
                onChange={() => toggleColumnFilter('department')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Department</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columnFilters.roles}
                onChange={() => toggleColumnFilter('roles')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Roles</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columnFilters.status}
                onChange={() => toggleColumnFilter('status')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Status</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columnFilters.lastLogin}
                onChange={() => toggleColumnFilter('lastLogin')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Last Login</span>
            </label>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                User
              </th>
              {columnFilters.jobTitle && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Job Title
                </th>
              )}
              {columnFilters.department && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Department
                </th>
              )}
              {columnFilters.roles && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Roles
                </th>
              )}
              {columnFilters.status && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Status
                </th>
              )}
              {columnFilters.lastLogin && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Last Login
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.map((user) => {
              const userName = user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name';
              const avatarColor = getAvatarColor(userName);
              const initials = user.first_name?.[0] || '?';
              const lastInitial = user.last_name?.[0] || '';

              return (
              <tr
                key={user.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => handleUserClick(user)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {user.profile_image_url ? (
                      <img
                        className="w-10 h-10 rounded-full"
                        src={`${process.env.REACT_APP_API_URL}${user.profile_image_url}`}
                        alt={userName}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full ${avatarColor.bg} flex items-center justify-center`}>
                        <span className={`text-sm font-medium ${avatarColor.text}`}>
                          {initials}{lastInitial}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{userName}</p>
                      <p className="text-xs text-gray-500">{user.email || 'No email'}</p>
                    </div>
                  </div>
                </td>
                {columnFilters.jobTitle && (
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.job_title || 'N/A'}
                  </td>
                )}
                {columnFilters.department && (
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.department || 'N/A'}
                  </td>
                )}
                {columnFilters.roles && (
                  <td className="px-4 py-3">
                    {getRolesBadges((user as any).roles)}
                  </td>
                )}
                {columnFilters.status && (
                  <td className="px-4 py-3">
                    <Badge status={user.status} />
                  </td>
                )}
                {columnFilters.lastLogin && (
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                  </td>
                )}
                <td className="px-4 py-3 text-right relative">
                  <button
                    onClick={(e) => toggleMenu(e, user.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-400" />
                  </button>
                  {openMenuId === user.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <div className="py-1">
                        <button
                          onClick={(e) => handleEditUser(e, user)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit User
                        </button>
                        <button
                          onClick={(e) => handleDeleteUser(e, user.id)}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete User
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <EmptyState
            icon={UsersIcon}
            title="No users found"
            description="No users found matching your criteria."
          />
        )}
      </div>

      {showForm && (
        <UserForm
          user={editingUser}
          roles={roles}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      )}
    </>
  );
};

export default UsersList;
